"use client";

import { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface Category {
  id: string;
  name: string;
  slug: string;
}

interface CategoryWithChildren {
  category: Category;
  children: Category[];
}

interface CategoriesAfficheSectionProps {
  locationCategories: CategoryWithChildren[];
  achatCategories: CategoryWithChildren[];
}

export function CategoriesAfficheSection({
  locationCategories,
  achatCategories,
}: CategoriesAfficheSectionProps) {
  const [activeTab, setActiveTab] = useState<"location" | "achat">("location");
  const [currentPage, setCurrentPage] = useState(0);

  // Sélectionner les catégories selon l'onglet actif
  const categories = activeTab === "location" ? locationCategories : achatCategories;

  // Show 4 categories per page
  const categoriesPerPage = 4;
  const totalPages = Math.ceil(categories.length / categoriesPerPage);

  const visibleCategories = categories.slice(
    currentPage * categoriesPerPage,
    (currentPage + 1) * categoriesPerPage
  );

  const maxChildrenPerColumn = 10;

  const handlePrev = () => {
    setCurrentPage((prev) => (prev > 0 ? prev - 1 : totalPages - 1));
  };

  const handleNext = () => {
    setCurrentPage((prev) => (prev < totalPages - 1 ? prev + 1 : 0));
  };

  // Reset page when switching tabs
  const handleTabChange = (tab: "location" | "achat") => {
    setActiveTab(tab);
    setCurrentPage(0);
  };

  return (
    <section className="py-12 bg-white">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <h2 className="text-2xl md:text-3xl font-bold text-black flex items-center gap-3">
            <Image src="/logo-vignette.png" alt="" width={32} height={32} className="h-8 w-8" />
            Nos catégories à l&apos;affiche
          </h2>

          <div className="flex items-center gap-3">
            {/* Toggle Location/Achat */}
            <div className="flex gap-2">
              <Button
                variant={activeTab === "location" ? "secondary" : "outline"}
                onClick={() => handleTabChange("location")}
                size="sm"
                className="rounded-full"
              >
                Location
              </Button>
              <Button
                variant={activeTab === "achat" ? "secondary" : "outline"}
                onClick={() => handleTabChange("achat")}
                size="sm"
                className="rounded-full"
              >
                Achat
              </Button>
            </div>

            {/* Navigation arrows */}
            <div className="flex gap-2">
              <button
                onClick={handlePrev}
                className="w-9 h-9 sm:w-10 sm:h-10 rounded-full border border-secondary flex items-center justify-center hover:bg-secondary/10 transition-colors cursor-pointer"
              >
                <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 text-secondary" />
              </button>
              <button
                onClick={handleNext}
                className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-secondary flex items-center justify-center hover:bg-secondary/90 transition-colors cursor-pointer"
              >
                <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </button>
            </div>
          </div>
        </div>

        {/* Categories Grid */}
        <div className="hidden lg:grid lg:grid-cols-4 lg:gap-8">
          {visibleCategories.map((cat) => (
            <div key={cat.category.id} className="flex flex-col border-l-2 border-secondary pl-4 first:border-l-0 first:pl-0">
              <Link
                href={`/categories/${cat.category.slug}?type=${activeTab}`}
                className="font-bold text-black hover:text-secondary transition-colors block mb-4 pb-2 border-b border-gray-200 text-sm lg:text-base"
              >
                {cat.category.name}
              </Link>
              <ul className="space-y-1.5">
                {cat.children.slice(0, maxChildrenPerColumn).map((child) => (
                  <li key={child.id}>
                    <Link
                      href={`/categories/${child.slug}?type=${activeTab}`}
                      className="text-sm text-gray-700 font-medium hover:text-secondary transition-colors leading-tight"
                    >
                      {child.name}
                    </Link>
                  </li>
                ))}
                {cat.children.length > maxChildrenPerColumn && (
                  <li>
                    <Link
                      href={`/categories/${cat.category.slug}?type=${activeTab}`}
                      className="text-sm text-secondary font-medium hover:underline"
                    >
                      Voir tout ({cat.children.length})
                    </Link>
                  </li>
                )}
              </ul>
            </div>
          ))}
        </div>

        {/* Mobile Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-6 lg:hidden">
          {visibleCategories.map((cat) => (
            <div key={cat.category.id} className="flex flex-col">
              <Link
                href={`/categories/${cat.category.slug}?type=${activeTab}`}
                className="font-bold text-black hover:text-secondary transition-colors block mb-4 pb-2 border-b-2 border-secondary text-sm"
              >
                {cat.category.name}
              </Link>
              <ul className="space-y-1">
                {cat.children.slice(0, maxChildrenPerColumn).map((child) => (
                  <li key={child.id}>
                    <Link
                      href={`/categories/${child.slug}?type=${activeTab}`}
                      className="text-sm text-gray-700 font-medium hover:text-secondary transition-colors leading-tight"
                    >
                      {child.name}
                    </Link>
                  </li>
                ))}
                {cat.children.length > maxChildrenPerColumn && (
                  <li>
                    <Link
                      href={`/categories/${cat.category.slug}?type=${activeTab}`}
                      className="text-sm text-secondary font-medium hover:underline"
                    >
                      Voir tout ({cat.children.length})
                    </Link>
                  </li>
                )}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
